/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import type { AxiosResponse } from "axios";
import { FailoverResponse, MaintenanceResponse, OverviewSites, ServiceStatus, UsageData } from "../Types";
import { getFailoverHistoryAPI, getMonitoringAPI, getOverviewAPI, getServiceStatusAPI, getServicesUsageAPI } from "kontrolEdge/views/requests/request";
import { dropCachedFetch, readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";
import { getOverviewEndpointBaseUrl } from "@shared/config/featureFlags";
import { isTenantScopeForbidden } from "@shared/helpers/tenantScope";

interface ManagerOverviewLoadingState {
  service: boolean;
  usage: boolean;
  failover: boolean;
  maintenance: boolean;
}

interface ManagerOverviewDataState {
  serviceStatus: ServiceStatus | null;
  usage: UsageData | null;
  failover: FailoverResponse | null;
  maintenance: MaintenanceResponse | null;
  /** Names, positions and DCI links of the scope's sites. Only the overview endpoint sends them. */
  sites: OverviewSites | null;
}

const EMPTY_OVERVIEW: ManagerOverviewDataState = {
  serviceStatus: null,
  usage: null,
  failover: null,
  maintenance: null,
  sites: null,
};

const ALL_LOADING: ManagerOverviewLoadingState = { service: true, usage: true, failover: true, maintenance: true };
const NOTHING_LOADING: ManagerOverviewLoadingState = { service: false, usage: false, failover: false, maintenance: false };

/**
 * Whether the tenant has any service at all. Every bucket the endpoint returns counts: a
 * BBaaS tenant whose only services are ports and SD-WAN hubs is not an empty tenant, and
 * leaving those two out rendered the empty state over a fully provisioned dashboard.
 */
function hasAnyService(serviceStatus: ServiceStatus | null): boolean {
  return (
    !!serviceStatus &&
    (
      (serviceStatus.ports?.length ?? 0) > 0 ||
      (serviceStatus.internet?.length ?? 0) > 0 ||
      (serviceStatus.datacenterInterconnect?.length ?? 0) > 0 ||
      (serviceStatus.cloudConnectivity?.length ?? 0) > 0 ||
      (serviceStatus.fwaas?.length ?? 0) > 0 ||
      (serviceStatus.sdwan?.length ?? 0) > 0
    )
  );
}

/** A fetched overview, and whether its service statuses could not be read. */
interface OverviewLoad {
  fresh: ManagerOverviewDataState;
  serviceFailed: boolean;
}

/** The four endpoints in parallel: what the Overview has always done. */
async function loadFromFourEndpoints(
  api: any,
  tenantId: number,
  signal: AbortSignal,
  cached: ManagerOverviewDataState | undefined
): Promise<OverviewLoad> {
  const [rService, rUsage, rFail, rMaint] = await Promise.allSettled([
    getServiceStatusAPI(api, tenantId, { signal }),
    getServicesUsageAPI(api, tenantId, { signal }),
    getFailoverHistoryAPI(api, tenantId, { signal }),
    getMonitoringAPI(api, tenantId, { signal }),
  ]);

  if (rService.status === "rejected") console.error("serviceStatus failed:", rService.reason);
  if (rUsage.status   === "rejected") console.error("usage failed:", rUsage.reason);
  if (rFail.status    === "rejected") console.error("failover failed:", rFail.reason);
  if (rMaint.status   === "rejected") console.error("maintenance failed:", rMaint.reason);

  // A tenant outside the caller's scope is refused as a whole: no section falls back
  // to what was cached for it.
  const denied = [rService, rUsage, rFail, rMaint].find(
    (r): r is PromiseRejectedResult => r.status === "rejected" && isTenantScopeForbidden(r.reason)
  );
  if (denied) throw denied.reason;

  // A request that failed on a refresh keeps the section it feeds on its last
  // good answer instead of blanking it.
  return {
    fresh: {
      serviceStatus: settledOr(rService, (r) => r.data?.data ?? null, cached?.serviceStatus ?? null),
      usage: settledOr(rUsage, (r) => r.data ?? null, cached?.usage ?? null),
      failover: settledOr(rFail, (r) => r.data?.data ?? null, cached?.failover ?? null),
      maintenance: settledOr(rMaint, (r) => r.data?.data ?? null, cached?.maintenance ?? null),
      sites: null,
    },
    serviceFailed: rService.status === "rejected",
  };
}

/**
 * The same four payloads from `GET /v1/kontroledge/overview` (CTX-868).
 *
 * The answer is only used when the backend resolved the tenant that was asked
 * for: an older backend answered a partner asking for a tenant it may not see
 * with its own data (getTenantId), and painting or caching that under the
 * asked-for key would show one tenant's overview under another's name. The
 * current backend refuses it with a 403 instead (CTX-866).
 */
async function loadFromOverviewEndpoint(
  api: any,
  baseUrl: string,
  tenantId: number,
  signal: AbortSignal,
  cached: ManagerOverviewDataState | undefined
): Promise<OverviewLoad> {
  const response = await getOverviewAPI(api, baseUrl, tenantId, { signal });
  const body = response.data?.data;
  if (Number(body?.meta?.tenantId) !== Number(tenantId)) {
    throw new Error(`Overview answered for tenant ${body?.meta?.tenantId}, asked for ${tenantId}`);
  }
  const errors: string[] = body?.meta?.errors ?? [];
  const partOr = <K extends keyof ManagerOverviewDataState>(part: K) =>
    errors.includes(part) ? cached?.[part] ?? null : body?.[part] ?? null;

  return {
    fresh: {
      serviceStatus: partOr("serviceStatus"),
      usage: partOr("usage"),
      failover: partOr("failover"),
      maintenance: partOr("maintenance"),
      sites: partOr("sites"),
    },
    serviceFailed: errors.includes("serviceStatus"),
  };
}

/** The value of a settled request, or — when it failed — the last good one. */
function settledOr<T>(
  result: PromiseSettledResult<AxiosResponse>,
  pick: (response: AxiosResponse) => T | null,
  fallback: T | null
): T | null {
  return result.status === "fulfilled" ? pick(result.value) : fallback;
}

export function useManagerOverviewData(api: any, tenantId: number) {
  // The last assembled overview for this tenant paints at once when the user comes back (a
  // view switch, Back); the four requests below still run and replace it. The overview's
  // only caller is ManagerOverview, so the cache lives here.
  const cacheKey = tenantId ? `kontroledge/overview?tenantId=${tenantId}` : null;

  const [data, setData] = useState<ManagerOverviewDataState>(
    () => readCachedFetch<ManagerOverviewDataState>(cacheKey) ?? EMPTY_OVERVIEW
  );
  const [isServiceFetched, setIsServiceFetched] = useState(
    () => readCachedFetch(cacheKey) !== undefined
  );
  // Starts loading when there is nothing to paint: the first frame would otherwise read as
  // an empty tenant before the fetch effect has had a chance to run.
  const [loading, setLoading] = useState<ManagerOverviewLoadingState>(() =>
    cacheKey && readCachedFetch(cacheKey) === undefined ? ALL_LOADING : NOTHING_LOADING
  );
  /** A cached overview is on screen and a newer one is on its way. */
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Whether the service-status request itself failed, as opposed to coming
   * back empty. Both used to collapse into `null` here, so a backend outage
   * rendered the "your tenant is currently empty" state to a customer with
   * dozens of provisioned services. The two need to stay distinguishable all
   * the way to the view.
   */
  const [hasServiceError, setHasServiceError] = useState(false);
  /**
   * The backend refused the tenant as outside the caller's scope (CTX-866). Nothing
   * of it is painted or cached: the view shows the refusal, not an empty tenant.
   */
  const [isOutOfScope, setIsOutOfScope] = useState(false);

  useEffect(() => {
    if (!cacheKey) return;

    const controller = new AbortController();
    const { signal } = controller;

    // Another tenant's overview never shows under this one: its cached answer, or skeletons.
    const cached = readCachedFetch<ManagerOverviewDataState>(cacheKey);
    setHasServiceError(false);
    setIsOutOfScope(false);
    // Set on both branches: an aborted run (the tenant moved) never reaches its `finally`.
    setIsRefreshing(Boolean(cached));
    if (cached) {
      setData(cached);
      setLoading(NOTHING_LOADING);
      setIsServiceFetched(true);
    } else {
      setData(EMPTY_OVERVIEW);
      setLoading(ALL_LOADING);
      setIsServiceFetched(false);
    }

    const overviewBaseUrl = getOverviewEndpointBaseUrl();

    (async () => {
      try {
        const { fresh, serviceFailed } = overviewBaseUrl
          ? await loadFromOverviewEndpoint(api, overviewBaseUrl, tenantId, signal, cached).catch(
              (err): OverviewLoad | Promise<OverviewLoad> => {
                if (signal.aborted || isTenantScopeForbidden(err)) throw err;
                // A backend without the endpoint yet (staging before its deploy, a
                // demo build) is not an outage: read the four endpoints as before.
                if (err?.response?.status === 404) return loadFromFourEndpoints(api, tenantId, signal, cached);
                // One request carries every part, so its failure is the
                // service-status failure too: "monitoring unavailable", not "empty".
                console.error("overview failed:", err);
                return { fresh: cached ?? EMPTY_OVERVIEW, serviceFailed: true };
              }
            )
          : await loadFromFourEndpoints(api, tenantId, signal, cached);

        // Single, clean state commit with the full payload
        if (!signal.aborted) {
          // Only an overview the page can stand on is cached: a failed service-status
          // request with nothing to fall back on is "monitoring unavailable", which the
          // next visit must ask again rather than repaint.
          if (!serviceFailed || cached) writeCachedFetch(cacheKey, fresh);
          setHasServiceError(serviceFailed && !cached);
          setData(fresh);
          setIsServiceFetched(true);
        }
      } catch (err) {
        if (isTenantScopeForbidden(err)) {
          // The cached overview is not this caller's to see: drop it, paint nothing.
          dropCachedFetch(cacheKey);
          if (!signal.aborted) {
            setData(EMPTY_OVERVIEW);
            setIsOutOfScope(true);
            setIsServiceFetched(true);
          }
        } else if (!signal.aborted) {
          console.error("ManagerOverview parallel fetch error:", err);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(NOTHING_LOADING);
          setIsRefreshing(false);
        }
      }
    })();

    return () => controller.abort();
  }, [api, tenantId, cacheKey]);

  const hasData = hasAnyService(data.serviceStatus);
  return { data, loading, hasData, isServiceFetched, hasServiceError, isOutOfScope, isRefreshing };
}
